/**
 * kmlParser.ts
 * -----------------------------------------------------------------------------
 * Production-grade KML parser built exclusively on the browser's native
 * DOMParser + standard DOM APIs (no external KML libraries).
 *
 * Designed to be consumed by a React Native Web application that renders
 * geometry through the Google Maps JavaScript API.
 *
 * The file performs a SINGLE depth-first traversal of the KML XML tree.
 * Styles (<Style>/<StyleMap>) are collected into shared registries as they
 * are encountered and are resolved lazily (once) via resolveFeatureStyle().
 * -----------------------------------------------------------------------------
 */

/* =============================================================================
 * SECTION 1: CORE / GEOMETRY / STYLE / OVERLAY / TIME / REGION TYPES
 * ========================================================================== */

/** A single geographic coordinate resolved from KML's "lon,lat[,alt]" format. */
export interface LatLngAlt {
  lat: number;
  lng: number;
  alt: number;
}

/** KML altitudeMode / gx:altitudeMode values normalized to a closed union. */
export type KmlAltitudeMode =
  | "clampToGround"
  | "relativeToGround"
  | "absolute";

/** A 2D screen/overlay vector (used by ScreenOverlay hotSpot/overlayXY/etc). */
export interface KmlVec2 {
  x: number;
  y: number;
  xunits: "fraction" | "pixels" | "insetPixels";
  yunits: "fraction" | "pixels" | "insetPixels";
}

/* ---------------------------- Geometry types ---------------------------- */

export interface KmlPoint {
  type: "Point";
  coordinates: LatLngAlt;
  altitudeMode: KmlAltitudeMode;
  extrude: boolean;
}

export interface KmlLineString {
  type: "LineString";
  coordinates: LatLngAlt[];
  altitudeMode: KmlAltitudeMode;
  extrude: boolean;
  tessellate: boolean;
}

export interface KmlLinearRing {
  type: "LinearRing";
  coordinates: LatLngAlt[];
  altitudeMode: KmlAltitudeMode;
  extrude: boolean;
  tessellate: boolean;
}

export interface KmlPolygon {
  type: "Polygon";
  outerBoundary: LatLngAlt[];
  innerBoundaries: LatLngAlt[][];
  altitudeMode: KmlAltitudeMode;
  extrude: boolean;
  tessellate: boolean;
}

export interface KmlMultiGeometry {
  type: "MultiGeometry";
  geometries: KmlGeometry[];
}

export type KmlGeometry =
  | KmlPoint
  | KmlLineString
  | KmlLinearRing
  | KmlPolygon
  | KmlMultiGeometry;

/* ----------------------------- Style types ------------------------------ */

export type KmlColorMode = "normal" | "random";

export interface KmlIconStyle {
  color: string | null;
  colorMode: KmlColorMode;
  scale: number;
  heading: number;
  iconHref: string | null;
  hotSpot: KmlVec2 | null;
}

export interface KmlLabelStyle {
  color: string | null;
  colorMode: KmlColorMode;
  scale: number;
}

export interface KmlLineStyle {
  color: string | null;
  colorMode: KmlColorMode;
  width: number;
}

export interface KmlPolyStyle {
  color: string | null;
  colorMode: KmlColorMode;
  fill: boolean;
  outline: boolean;
}

export interface KmlBalloonStyle {
  bgColor: string | null;
  textColor: string | null;
  text: string | null;
  displayMode: "default" | "hide";
}

export interface KmlListStyle {
  listItemType: "check" | "checkOffOnly" | "checkHideChildren" | "radioFolder";
  bgColor: string | null;
}

export interface KmlStyle {
  id: string | null;
  iconStyle: KmlIconStyle | null;
  labelStyle: KmlLabelStyle | null;
  lineStyle: KmlLineStyle | null;
  polyStyle: KmlPolyStyle | null;
  balloonStyle: KmlBalloonStyle | null;
  listStyle: KmlListStyle | null;
}

export interface KmlStyleMapPair {
  key: "normal" | "highlight";
  styleUrl: string | null;
  style: KmlStyle | null;
}

export interface KmlStyleMap {
  id: string | null;
  pairs: KmlStyleMapPair[];
}

/* ----------------------------- Camera types ------------------------------ */

export interface KmlLookAt {
  longitude: number;
  latitude: number;
  altitude: number;
  heading: number;
  tilt: number;
  range: number;
  altitudeMode: KmlAltitudeMode;
}

export interface KmlCamera {
  longitude: number;
  latitude: number;
  altitude: number;
  heading: number;
  tilt: number;
  roll: number;
  altitudeMode: KmlAltitudeMode;
}

/* ----------------------------- Region types ------------------------------ */

export interface KmlLod {
  minLodPixels: number;
  maxLodPixels: number;
  minFadeExtent: number;
  maxFadeExtent: number;
}

export interface KmlRegion {
  latLonAltBox: {
    north: number;
    south: number;
    east: number;
    west: number;
    minAltitude: number;
    maxAltitude: number;
    altitudeMode: KmlAltitudeMode;
  };
  lod: KmlLod | null;
}

/* ------------------------------ Time types -------------------------------- */

export interface KmlTimeSpan {
  begin: string | null;
  end: string | null;
}

export interface KmlTimeStamp {
  when: string | null;
}

/* -------------------------- Extended data types --------------------------- */

export interface KmlData {
  name: string;
  displayName: string | null;
  value: string;
}

export interface KmlSchemaData {
  schemaUrl: string | null;
  data: { name: string; value: string }[];
}

export interface KmlExtendedData {
  data: KmlData[];
  schemaData: KmlSchemaData[];
}

/* ------------------------------ Overlay types ------------------------------ */

export interface KmlIconRef {
  href: string | null;
}

export interface KmlGroundOverlay {
  type: "GroundOverlay";
  id: string | null;
  name: string;
  description: string;
  visibility: boolean;
  icon: KmlIconRef;
  latLonBox: {
    north: number;
    south: number;
    east: number;
    west: number;
    rotation: number;
  };
  altitude: number;
  altitudeMode: KmlAltitudeMode;
}

export interface KmlScreenOverlay {
  type: "ScreenOverlay";
  id: string | null;
  name: string;
  description: string;
  visibility: boolean;
  icon: KmlIconRef;
  overlayXY: KmlVec2 | null;
  screenXY: KmlVec2 | null;
  rotationXY: KmlVec2 | null;
  size: KmlVec2 | null;
  rotation: number;
}

export interface KmlPhotoOverlay {
  type: "PhotoOverlay";
  id: string | null;
  name: string;
  description: string;
  visibility: boolean;
  icon: KmlIconRef;
  rotation: number;
  drawOrder: number;
  viewVolume: {
    leftFov: number;
    rightFov: number;
    bottomFov: number;
    topFov: number;
    near: number;
  } | null;
  shape: "rectangle" | "cylinder" | "sphere";
  point: KmlPoint | null;
}

export type KmlOverlay = KmlGroundOverlay | KmlScreenOverlay | KmlPhotoOverlay;

/* ------------------------------ Feature types ------------------------------ */

/** Rendering-friendly, flattened representation consumed by map layers. */
export interface KmlPlacemarkProperties {
  name: string;
  description: string;
  visibility: boolean;
}

export interface KmlPlacemark {
  id: string;
  type: "Placemark";
  /** Resolved geometry (null if the placemark had no renderable geometry). */
  geometry: KmlGeometry | null;
  /** Fully resolved style (inline + styleUrl/StyleMap merged). */
  style: KmlStyle | null;
  /** Flattened, render-ready properties. */
  properties: KmlPlacemarkProperties;
  /** Parsed <ExtendedData>, if present. */
  extendedData: KmlExtendedData | null;
  /** Raw styleUrl reference, preserved for debugging/re-resolution. */
  styleUrl: string | null;
  timeSpan: KmlTimeSpan | null;
  timeStamp: KmlTimeStamp | null;
  lookAt: KmlLookAt | null;
  camera: KmlCamera | null;
  region: KmlRegion | null;
}

export interface KmlFolder {
  id: string | null;
  type: "Folder";
  name: string;
  description: string;
  visibility: boolean;
  open: boolean;
  folders: KmlFolder[];
  placemarks: KmlPlacemark[];
  overlays: KmlOverlay[];
}

/** Generic union usable for uniform tree-walking over parsed features. */
export type KmlFeature = KmlPlacemark | KmlFolder | KmlOverlay;

export interface KmlDocument {
  id: string | null;
  type: "Document";
  name: string;
  description: string;
  visibility: boolean;
  open: boolean;
  folders: KmlFolder[];
  placemarks: KmlPlacemark[];
  overlays: KmlOverlay[];
  styles: Record<string, KmlStyle>;
  styleMaps: Record<string, KmlStyleMap>;
  lookAt: KmlLookAt | null;
  camera: KmlCamera | null;
}

/** Shape returned by parseKmlDocument(). */
export interface KmlParseResult {
  document: KmlDocument | null;
  folders: KmlFolder[];
  placemarks: KmlPlacemark[];
  styles: Record<string, KmlStyle>;
  styleMaps: Record<string, KmlStyleMap>;
  overlays: KmlOverlay[];
  lookAt: KmlLookAt | null;
  camera: KmlCamera | null;
}

/** Mutable registries threaded through the single tree traversal. */
interface StyleRegistries {
  styles: Record<string, KmlStyle>;
  styleMaps: Record<string, KmlStyleMap>;
}

/* =============================================================================
 * SECTION 2: GENERIC DOM UTILITIES
 * ========================================================================== */

const ELEMENT_NODE = 1;

/**
 * Returns the direct child elements of `parent` whose local tag name matches
 * `tagName`. Namespace-safe: compares against `localName` first, falling
 * back to `tagName` for parsers that don't populate localName.
 */
export function getChildren(parent: Element | null, tagName: string): Element[] {
  if (!parent) return [];
  const results: Element[] = [];
  const nodes = parent.childNodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.nodeType === ELEMENT_NODE) {
      const el = node as Element;
      const localName = (el.localName || el.tagName || "").toLowerCase();
      if (localName === tagName.toLowerCase()) {
        results.push(el);
      }
    }
  }
  return results;
}

/** Returns the first direct child element matching `tagName`, or null. */
export function getChild(parent: Element | null, tagName: string): Element | null {
  const children = getChildren(parent, tagName);
  return children.length > 0 ? children[0] : null;
}

/**
 * Returns trimmed text content. If `tagName` is supplied, first resolves the
 * named child element and returns its text; otherwise returns the text of
 * `parent` itself.
 */
export function getText(parent: Element | null, tagName?: string): string {
  if (!parent) return "";
  if (tagName) {
    const child = getChild(parent, tagName);
    return child ? (child.textContent || "").trim() : "";
  }
  return (parent.textContent || "").trim();
}

/** Parses KML boolean fields ("0"/"1", "true"/"false") with a safe default. */
export function parseBoolean(value: string | null | undefined, fallback = false): boolean {
  if (value === null || value === undefined || value === "") return fallback;
  const normalized = value.trim().toLowerCase();
  return normalized === "1" || normalized === "true";
}

/** Parses a float, returning `fallback` for empty/invalid input. */
export function parseFloatSafe(value: string | null | undefined, fallback = 0): number {
  if (value === null || value === undefined || value.trim() === "") return fallback;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/** Parses an int, returning `fallback` for empty/invalid input. */
export function parseIntSafe(value: string | null | undefined, fallback = 0): number {
  if (value === null || value === undefined || value.trim() === "") return fallback;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Parses a KML <coordinates> text block. Each whitespace-separated token is
 * "lon,lat[,alt]". Returns an array of {lat,lng,alt} objects.
 */
export function parseCoordinates(text: string | null | undefined): LatLngAlt[] {
  if (!text) return [];
  const trimmed = text.trim();
  if (trimmed === "") return [];

  const tuples = trimmed.split(/\s+/);
  const result: LatLngAlt[] = [];

  for (let i = 0; i < tuples.length; i++) {
    const parts = tuples[i].split(",");
    if (parts.length < 2) continue;
    const lng = parseFloatSafe(parts[0], NaN);
    const lat = parseFloatSafe(parts[1], NaN);
    if (Number.isNaN(lng) || Number.isNaN(lat)) continue;
    const alt = parts.length >= 3 ? parseFloatSafe(parts[2], 0) : 0;
    result.push({ lat, lng, alt });
  }

  return result;
}

/**
 * Converts a KML color string (aabbggrr, hex, no leading '#') into a CSS-style
 * "#RRGGBBAA" hex string. Returns null for missing/invalid input.
 */
export function parseColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const hex = value.trim().replace(/^#/, "");
  if (hex.length !== 8 || /[^0-9a-fA-F]/.test(hex)) return null;

  const aa = hex.substring(0, 2);
  const bb = hex.substring(2, 4);
  const gg = hex.substring(4, 6);
  const rr = hex.substring(6, 8);

  return `#${rr}${gg}${bb}${aa}`.toUpperCase();
}

/** Normalizes an altitudeMode string to the closed KmlAltitudeMode union. */
export function parseAltitudeMode(value: string | null | undefined): KmlAltitudeMode {
  if (value === "relativeToGround" || value === "absolute") return value;
  return "clampToGround";
}

/** Parses a KML color-mode field ("normal"/"random") with a safe default. */
function parseColorMode(value: string | null | undefined): KmlColorMode {
  return value === "random" ? "random" : "normal";
}

/** Parses a <hotSpot>/<overlayXY>/<screenXY>/<rotationXY>/<size> style vec2. */
function parseVec2(el: Element | null): KmlVec2 | null {
  if (!el) return null;
  const xunits = (el.getAttribute("xunits") || "fraction") as KmlVec2["xunits"];
  const yunits = (el.getAttribute("yunits") || "fraction") as KmlVec2["yunits"];
  return {
    x: parseFloatSafe(el.getAttribute("x"), 0),
    y: parseFloatSafe(el.getAttribute("y"), 0),
    xunits,
    yunits,
  };
}

/** Strips a leading '#' from a styleUrl/schemaUrl reference. */
function stripHash(value: string | null | undefined): string {
  if (!value) return "";
  return value.trim().replace(/^#/, "");
}

/* =============================================================================
 * SECTION 3: STYLE PARSERS
 * ========================================================================== */

/** Parses an <IconStyle> element. */
export function parseIconStyle(el: Element | null): KmlIconStyle | null {
  if (!el) return null;
  const iconEl = getChild(el, "Icon");
  return {
    color: parseColor(getText(el, "color")) ,
    colorMode: parseColorMode(getText(el, "colorMode")),
    scale: parseFloatSafe(getText(el, "scale"), 1),
    heading: parseFloatSafe(getText(el, "heading"), 0),
    iconHref: iconEl ? getText(iconEl, "href") || null : null,
    hotSpot: parseVec2(getChild(el, "hotSpot")),
  };
}

/** Parses a <LabelStyle> element. */
export function parseLabelStyle(el: Element | null): KmlLabelStyle | null {
  if (!el) return null;
  return {
    color: parseColor(getText(el, "color")),
    colorMode: parseColorMode(getText(el, "colorMode")),
    scale: parseFloatSafe(getText(el, "scale"), 1),
  };
}

/** Parses a <LineStyle> element. */
export function parseLineStyle(el: Element | null): KmlLineStyle | null {
  if (!el) return null;
  return {
    color: parseColor(getText(el, "color")),
    colorMode: parseColorMode(getText(el, "colorMode")),
    width: parseFloatSafe(getText(el, "width"), 1),
  };
}

/** Parses a <PolyStyle> element. */
export function parsePolyStyle(el: Element | null): KmlPolyStyle | null {
  if (!el) return null;
  return {
    color: parseColor(getText(el, "color")),
    colorMode: parseColorMode(getText(el, "colorMode")),
    fill: parseBoolean(getText(el, "fill") || null, true),
    outline: parseBoolean(getText(el, "outline") || null, true),
  };
}

/** Parses a <BalloonStyle> element. */
export function parseBalloonStyle(el: Element | null): KmlBalloonStyle | null {
  if (!el) return null;
  const displayModeText = getText(el, "displayMode");
  return {
    bgColor: parseColor(getText(el, "bgColor")),
    textColor: parseColor(getText(el, "textColor")),
    text: getText(el, "text") || null,
    displayMode: displayModeText === "hide" ? "hide" : "default",
  };
}

/** Parses a <ListStyle> element. */
export function parseListStyle(el: Element | null): KmlListStyle | null {
  if (!el) return null;
  const listItemTypeText = getText(el, "listItemType");
  const validTypes: KmlListStyle["listItemType"][] = [
    "check",
    "checkOffOnly",
    "checkHideChildren",
    "radioFolder",
  ];
  const listItemType = (validTypes as string[]).includes(listItemTypeText)
    ? (listItemTypeText as KmlListStyle["listItemType"])
    : "check";
  return {
    listItemType,
    bgColor: parseColor(getText(el, "bgColor")),
  };
}

/** Parses a <Style> element (inline or top-level, referenced by id). */
export function parseStyle(el: Element | null): KmlStyle {
  const id = el ? el.getAttribute("id") : null;
  return {
    id: id || null,
    iconStyle: parseIconStyle(getChild(el, "IconStyle")),
    labelStyle: parseLabelStyle(getChild(el, "LabelStyle")),
    lineStyle: parseLineStyle(getChild(el, "LineStyle")),
    polyStyle: parsePolyStyle(getChild(el, "PolyStyle")),
    balloonStyle: parseBalloonStyle(getChild(el, "BalloonStyle")),
    listStyle: parseListStyle(getChild(el, "ListStyle")),
  };
}

/** Parses a <StyleMap> element (Normal/Highlight key -> styleUrl or inline Style). */
export function parseStyleMap(el: Element | null): KmlStyleMap {
  const id = el ? el.getAttribute("id") : null;
  const pairs: KmlStyleMapPair[] = [];

  const pairEls = getChildren(el, "Pair");
  for (let i = 0; i < pairEls.length; i++) {
    const pairEl = pairEls[i];
    const keyText = getText(pairEl, "key").toLowerCase();
    const key: KmlStyleMapPair["key"] = keyText === "highlight" ? "highlight" : "normal";
    const styleUrlText = getText(pairEl, "styleUrl");
    const inlineStyleEl = getChild(pairEl, "Style");
    pairs.push({
      key,
      styleUrl: styleUrlText || null,
      style: inlineStyleEl ? parseStyle(inlineStyleEl) : null,
    });
  }

  return { id: id || null, pairs };
}

/* =============================================================================
 * SECTION 4: GEOMETRY PARSERS
 * ========================================================================== */

/** Parses a <Point> element. */
export function parsePoint(el: Element): KmlPoint {
  const coords = parseCoordinates(getText(el, "coordinates"));
  return {
    type: "Point",
    coordinates: coords.length > 0 ? coords[0] : { lat: 0, lng: 0, alt: 0 },
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
    extrude: parseBoolean(getText(el, "extrude") || null, false),
  };
}

/** Parses a <LineString> element. */
export function parseLineString(el: Element): KmlLineString {
  return {
    type: "LineString",
    coordinates: parseCoordinates(getText(el, "coordinates")),
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
    extrude: parseBoolean(getText(el, "extrude") || null, false),
    tessellate: parseBoolean(getText(el, "tessellate") || null, false),
  };
}

/** Parses a <LinearRing> element. */
export function parseLinearRing(el: Element): KmlLinearRing {
  return {
    type: "LinearRing",
    coordinates: parseCoordinates(getText(el, "coordinates")),
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
    extrude: parseBoolean(getText(el, "extrude") || null, false),
    tessellate: parseBoolean(getText(el, "tessellate") || null, false),
  };
}

/** Parses a <Polygon> element, including outer/inner boundary rings. */
export function parsePolygon(el: Element): KmlPolygon {
  const outerBoundaryIsEl = getChild(el, "outerBoundaryIs");
  const outerRingEl = getChild(outerBoundaryIsEl, "LinearRing");
  const outerBoundary = outerRingEl ? parseCoordinates(getText(outerRingEl, "coordinates")) : [];

  const innerBoundaryIsEls = getChildren(el, "innerBoundaryIs");
  const innerBoundaries: LatLngAlt[][] = [];
  for (let i = 0; i < innerBoundaryIsEls.length; i++) {
    const innerRingEl = getChild(innerBoundaryIsEls[i], "LinearRing");
    if (innerRingEl) {
      innerBoundaries.push(parseCoordinates(getText(innerRingEl, "coordinates")));
    }
  }

  return {
    type: "Polygon",
    outerBoundary,
    innerBoundaries,
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
    extrude: parseBoolean(getText(el, "extrude") || null, false),
    tessellate: parseBoolean(getText(el, "tessellate") || null, false),
  };
}

/**
 * Dispatches a geometry element to its dedicated parser based on tag name.
 * Used by parseMultiGeometry() and parsePlacemark() to avoid duplicating
 * detection logic.
 */
function parseGeometryElement(el: Element): KmlGeometry | null {
  const tag = (el.localName || el.tagName || "").toLowerCase();
  switch (tag) {
    case "point":
      return parsePoint(el);
    case "linestring":
      return parseLineString(el);
    case "linearring":
      return parseLinearRing(el);
    case "polygon":
      return parsePolygon(el);
    case "multigeometry":
      return parseMultiGeometry(el);
    default:
      return null;
  }
}

/** Parses a <MultiGeometry> element, recursively resolving child geometries. */
export function parseMultiGeometry(el: Element): KmlMultiGeometry {
  const geometries: KmlGeometry[] = [];
  const nodes = el.childNodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.nodeType === ELEMENT_NODE) {
      const geometry = parseGeometryElement(node as Element);
      if (geometry) geometries.push(geometry);
    }
  }
  return { type: "MultiGeometry", geometries };
}

/**
 * Scans the direct children of a feature element (e.g. Placemark) and
 * returns the first geometry found, or null if none is present.
 */
function findGeometry(el: Element): KmlGeometry | null {
  const geometryTags = ["Point", "LineString", "LinearRing", "Polygon", "MultiGeometry"];
  for (let i = 0; i < geometryTags.length; i++) {
    const child = getChild(el, geometryTags[i]);
    if (child) return parseGeometryElement(child);
  }
  return null;
}

/* =============================================================================
 * SECTION 5: OVERLAY PARSERS
 * ========================================================================== */

function parseIconRef(el: Element | null): KmlIconRef {
  const iconEl = getChild(el, "Icon");
  return { href: iconEl ? getText(iconEl, "href") || null : null };
}

/** Parses a <GroundOverlay> element. */
export function parseGroundOverlay(el: Element): KmlGroundOverlay {
  const latLonBoxEl = getChild(el, "LatLonBox");
  return {
    type: "GroundOverlay",
    id: el.getAttribute("id") || null,
    name: getText(el, "name"),
    description: getText(el, "description"),
    visibility: parseBoolean(getText(el, "visibility") || null, true),
    icon: parseIconRef(el),
    latLonBox: {
      north: parseFloatSafe(getText(latLonBoxEl, "north"), 0),
      south: parseFloatSafe(getText(latLonBoxEl, "south"), 0),
      east: parseFloatSafe(getText(latLonBoxEl, "east"), 0),
      west: parseFloatSafe(getText(latLonBoxEl, "west"), 0),
      rotation: parseFloatSafe(getText(latLonBoxEl, "rotation"), 0),
    },
    altitude: parseFloatSafe(getText(el, "altitude"), 0),
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
  };
}

/** Parses a <ScreenOverlay> element. */
export function parseScreenOverlay(el: Element): KmlScreenOverlay {
  return {
    type: "ScreenOverlay",
    id: el.getAttribute("id") || null,
    name: getText(el, "name"),
    description: getText(el, "description"),
    visibility: parseBoolean(getText(el, "visibility") || null, true),
    icon: parseIconRef(el),
    overlayXY: parseVec2(getChild(el, "overlayXY")),
    screenXY: parseVec2(getChild(el, "screenXY")),
    rotationXY: parseVec2(getChild(el, "rotationXY")),
    size: parseVec2(getChild(el, "size")),
    rotation: parseFloatSafe(getText(el, "rotation"), 0),
  };
}

/** Parses a <PhotoOverlay> element. */
export function parsePhotoOverlay(el: Element): KmlPhotoOverlay {
  const viewVolumeEl = getChild(el, "ViewVolume");
  const shapeText = getText(el, "shape");
  const validShapes: KmlPhotoOverlay["shape"][] = ["rectangle", "cylinder", "sphere"];
  const shape = (validShapes as string[]).includes(shapeText)
    ? (shapeText as KmlPhotoOverlay["shape"])
    : "rectangle";

  const pointEl = getChild(el, "Point");

  return {
    type: "PhotoOverlay",
    id: el.getAttribute("id") || null,
    name: getText(el, "name"),
    description: getText(el, "description"),
    visibility: parseBoolean(getText(el, "visibility") || null, true),
    icon: parseIconRef(el),
    rotation: parseFloatSafe(getText(el, "rotation"), 0),
    drawOrder: parseIntSafe(getText(el, "drawOrder"), 0),
    viewVolume: viewVolumeEl
      ? {
          leftFov: parseFloatSafe(getText(viewVolumeEl, "leftFov"), 0),
          rightFov: parseFloatSafe(getText(viewVolumeEl, "rightFov"), 0),
          bottomFov: parseFloatSafe(getText(viewVolumeEl, "bottomFov"), 0),
          topFov: parseFloatSafe(getText(viewVolumeEl, "topFov"), 0),
          near: parseFloatSafe(getText(viewVolumeEl, "near"), 0),
        }
      : null,
    shape,
    point: pointEl ? parsePoint(pointEl) : null,
  };
}

/**
 * Dispatches an overlay element to its dedicated parser based on tag name.
 */
function parseOverlayElement(el: Element): KmlOverlay | null {
  const tag = (el.localName || el.tagName || "").toLowerCase();
  switch (tag) {
    case "groundoverlay":
      return parseGroundOverlay(el);
    case "screenoverlay":
      return parseScreenOverlay(el);
    case "photooverlay":
      return parsePhotoOverlay(el);
    default:
      return null;
  }
}

/* =============================================================================
 * SECTION 6: CAMERA PARSERS
 * ========================================================================== */

/** Parses a <LookAt> element. */
export function parseLookAt(el: Element | null): KmlLookAt | null {
  if (!el) return null;
  return {
    longitude: parseFloatSafe(getText(el, "longitude"), 0),
    latitude: parseFloatSafe(getText(el, "latitude"), 0),
    altitude: parseFloatSafe(getText(el, "altitude"), 0),
    heading: parseFloatSafe(getText(el, "heading"), 0),
    tilt: parseFloatSafe(getText(el, "tilt"), 0),
    range: parseFloatSafe(getText(el, "range"), 0),
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
  };
}

/** Parses a <Camera> element. */
export function parseCamera(el: Element | null): KmlCamera | null {
  if (!el) return null;
  return {
    longitude: parseFloatSafe(getText(el, "longitude"), 0),
    latitude: parseFloatSafe(getText(el, "latitude"), 0),
    altitude: parseFloatSafe(getText(el, "altitude"), 0),
    heading: parseFloatSafe(getText(el, "heading"), 0),
    tilt: parseFloatSafe(getText(el, "tilt"), 0),
    roll: parseFloatSafe(getText(el, "roll"), 0),
    altitudeMode: parseAltitudeMode(getText(el, "altitudeMode")),
  };
}

/* =============================================================================
 * SECTION 7: TIME PARSERS
 * ========================================================================== */

/** Parses a <TimeSpan> element. */
export function parseTimeSpan(el: Element | null): KmlTimeSpan | null {
  if (!el) return null;
  return {
    begin: getText(el, "begin") || null,
    end: getText(el, "end") || null,
  };
}

/** Parses a <TimeStamp> element. */
export function parseTimeStamp(el: Element | null): KmlTimeStamp | null {
  if (!el) return null;
  return { when: getText(el, "when") || null };
}

/* =============================================================================
 * SECTION 8: REGION PARSER
 * ========================================================================== */

function parseLod(el: Element | null): KmlLod | null {
  if (!el) return null;
  return {
    minLodPixels: parseFloatSafe(getText(el, "minLodPixels"), 0),
    maxLodPixels: parseFloatSafe(getText(el, "maxLodPixels"), -1),
    minFadeExtent: parseFloatSafe(getText(el, "minFadeExtent"), 0),
    maxFadeExtent: parseFloatSafe(getText(el, "maxFadeExtent"), 0),
  };
}

/** Parses a <Region> element (LatLonAltBox + Lod). */
export function parseRegion(el: Element | null): KmlRegion | null {
  if (!el) return null;
  const boxEl = getChild(el, "LatLonAltBox");
  return {
    latLonAltBox: {
      north: parseFloatSafe(getText(boxEl, "north"), 0),
      south: parseFloatSafe(getText(boxEl, "south"), 0),
      east: parseFloatSafe(getText(boxEl, "east"), 0),
      west: parseFloatSafe(getText(boxEl, "west"), 0),
      minAltitude: parseFloatSafe(getText(boxEl, "minAltitude"), 0),
      maxAltitude: parseFloatSafe(getText(boxEl, "maxAltitude"), 0),
      altitudeMode: parseAltitudeMode(getText(boxEl, "altitudeMode")),
    },
    lod: parseLod(getChild(el, "Lod")),
  };
}

/* =============================================================================
 * SECTION 9: EXTENDED DATA PARSER
 * ========================================================================== */

/** Parses an <ExtendedData> element (Data + SchemaData/SimpleData children). */
export function parseExtendedData(el: Element | null): KmlExtendedData | null {
  if (!el) return null;

  const dataEls = getChildren(el, "Data");
  const data: KmlData[] = dataEls.map((dataEl) => ({
    name: dataEl.getAttribute("name") || "",
    displayName: getText(dataEl, "displayName") || null,
    value: getText(dataEl, "value"),
  }));

  const schemaDataEls = getChildren(el, "SchemaData");
  const schemaData: KmlSchemaData[] = schemaDataEls.map((schemaEl) => {
    const simpleDataEls = getChildren(schemaEl, "SimpleData");
    return {
      schemaUrl: schemaEl.getAttribute("schemaUrl") || null,
      data: simpleDataEls.map((simpleEl) => ({
        name: simpleEl.getAttribute("name") || "",
        value: getText(simpleEl),
      })),
    };
  });

  return { data, schemaData };
}

/* =============================================================================
 * SECTION 10: STYLE RESOLUTION
 * ========================================================================== */

/** Looks up a registered <Style> by id (with or without leading '#'). */
export function resolveStyle(
  styleId: string | null | undefined,
  styles: Record<string, KmlStyle>
): KmlStyle | null {
  if (!styleId) return null;
  const id = stripHash(styleId);
  return styles[id] || null;
}

/**
 * Resolves a <StyleMap> by id, returning the style referenced by the given
 * pair key ("normal" by default). Follows a styleUrl -> Style/StyleMap chain
 * with a recursion guard against malformed/cyclic references.
 */
export function resolveStyleMap(
  mapId: string | null | undefined,
  styleMaps: Record<string, KmlStyleMap>,
  styles: Record<string, KmlStyle>,
  key: "normal" | "highlight" = "normal",
  _visited: Set<string> = new Set()
): KmlStyle | null {
  if (!mapId) return null;
  const id = stripHash(mapId);
  if (_visited.has(id)) return null;
  _visited.add(id);

  const styleMap = styleMaps[id];
  if (!styleMap) return null;

  const pair =
    styleMap.pairs.find((p) => p.key === key) ||
    styleMap.pairs.find((p) => p.key === "normal");
  if (!pair) return null;

  if (pair.style) return pair.style;

  if (pair.styleUrl) {
    const refId = stripHash(pair.styleUrl);
    if (styles[refId]) return styles[refId];
    if (styleMaps[refId]) return resolveStyleMap(refId, styleMaps, styles, key, _visited);
  }

  return null;
}

/** Merges two styles field-by-field; `override` values win when present. */
function mergeStyles(base: KmlStyle | null, override: KmlStyle | null): KmlStyle | null {
  if (!base && !override) return null;
  if (!base) return override;
  if (!override) return base;

  return {
    id: override.id || base.id,
    iconStyle: override.iconStyle || base.iconStyle,
    labelStyle: override.labelStyle || base.labelStyle,
    lineStyle: override.lineStyle || base.lineStyle,
    polyStyle: override.polyStyle || base.polyStyle,
    balloonStyle: override.balloonStyle || base.balloonStyle,
    listStyle: override.listStyle || base.listStyle,
  };
}

/**
 * Resolves the final, merged style for a feature given its raw styleUrl
 * reference and/or inline <Style> element. Precedence: inline style
 * overrides the styleUrl-referenced style (which may itself be a StyleMap's
 * "normal" resolution), matching Google Earth's inheritance behavior.
 */
export function resolveFeatureStyle(
  styleUrl: string | null | undefined,
  inlineStyle: KmlStyle | null,
  styles: Record<string, KmlStyle>,
  styleMaps: Record<string, KmlStyleMap>
): KmlStyle | null {
  let base: KmlStyle | null = null;

  if (styleUrl) {
    const id = stripHash(styleUrl);
    if (styles[id]) {
      base = styles[id];
    } else if (styleMaps[id]) {
      base = resolveStyleMap(id, styleMaps, styles, "normal");
    }
  }

  return mergeStyles(base, inlineStyle);
}

/* =============================================================================
 * SECTION 11: FEATURE PARSERS (Placemark / Folder / Document)
 * ========================================================================== */

let _placemarkAutoId = 0;

/**
 * Parses a <Placemark> element into both its raw KML fields and a flattened
 * rendering-ready shape ({id,type,geometry,style,properties,extendedData}).
 * Automatically detects Point / LineString / Polygon / MultiGeometry and
 * resolves style via styleUrl + inline <Style> + registered StyleMaps.
 */
export function parsePlacemark(
  el: Element,
  registries: StyleRegistries
): KmlPlacemark {
  const id = el.getAttribute("id") || `placemark_${_placemarkAutoId++}`;
  const name = getText(el, "name");
  const description = getText(el, "description");
  const visibility = parseBoolean(getText(el, "visibility") || null, true);

  const styleUrl = getText(el, "styleUrl") || null;
  const inlineStyleEl = getChild(el, "Style");
  const inlineStyle = inlineStyleEl ? parseStyle(inlineStyleEl) : null;

  const resolvedStyle = resolveFeatureStyle(
    styleUrl,
    inlineStyle,
    registries.styles,
    registries.styleMaps
  );

  const geometry = findGeometry(el);
  const extendedData = parseExtendedData(getChild(el, "ExtendedData"));

  return {
    id,
    type: "Placemark",
    geometry,
    style: resolvedStyle,
    properties: { name, description, visibility },
    extendedData,
    styleUrl,
    timeSpan: parseTimeSpan(getChild(el, "TimeSpan")),
    timeStamp: parseTimeStamp(getChild(el, "TimeStamp")),
    lookAt: parseLookAt(getChild(el, "LookAt")),
    camera: parseCamera(getChild(el, "Camera")),
    region: parseRegion(getChild(el, "Region")),
  };
}

/**
 * Registers any direct-child <Style>/<StyleMap> elements of `el` into the
 * shared registries. Called once per container (Document/Folder) before
 * that container's Placemarks/Folders are parsed, so styleUrl references
 * resolve correctly without re-walking the whole XML tree.
 */
function registerLocalStyles(el: Element, registries: StyleRegistries): void {
  const styleEls = getChildren(el, "Style");
  for (let i = 0; i < styleEls.length; i++) {
    const style = parseStyle(styleEls[i]);
    if (style.id) registries.styles[style.id] = style;
  }

  const styleMapEls = getChildren(el, "StyleMap");
  for (let i = 0; i < styleMapEls.length; i++) {
    const styleMap = parseStyleMap(styleMapEls[i]);
    if (styleMap.id) registries.styleMaps[styleMap.id] = styleMap;
  }
}

/**
 * Parses a <Folder> element, recursively supporting nested folders. Styles
 * local to the folder are registered before children are processed.
 */
export function parseFolder(el: Element, registries: StyleRegistries): KmlFolder {
  registerLocalStyles(el, registries);

  const folders: KmlFolder[] = [];
  const placemarks: KmlPlacemark[] = [];
  const overlays: KmlOverlay[] = [];

  const nodes = el.childNodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.nodeType !== ELEMENT_NODE) continue;
    const child = node as Element;
    const tag = (child.localName || child.tagName || "").toLowerCase();

    if (tag === "folder") {
      folders.push(parseFolder(child, registries));
    } else if (tag === "placemark") {
      placemarks.push(parsePlacemark(child, registries));
    } else if (tag === "groundoverlay" || tag === "screenoverlay" || tag === "photooverlay") {
      const overlay = parseOverlayElement(child);
      if (overlay) overlays.push(overlay);
    }
  }

  return {
    id: el.getAttribute("id") || null,
    type: "Folder",
    name: getText(el, "name"),
    description: getText(el, "description"),
    visibility: parseBoolean(getText(el, "visibility") || null, true),
    open: parseBoolean(getText(el, "open") || null, false),
    folders,
    placemarks,
    overlays,
  };
}

/**
 * Parses a <Document> element: registers top-level Styles/StyleMaps, then
 * walks direct children once, dispatching Folders/Placemarks/Overlays.
 */
export function parseDocument(el: Element, registries: StyleRegistries): KmlDocument {
  registerLocalStyles(el, registries);

  const folders: KmlFolder[] = [];
  const placemarks: KmlPlacemark[] = [];
  const overlays: KmlOverlay[] = [];

  const nodes = el.childNodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.nodeType !== ELEMENT_NODE) continue;
    const child = node as Element;
    const tag = (child.localName || child.tagName || "").toLowerCase();

    if (tag === "folder") {
      folders.push(parseFolder(child, registries));
    } else if (tag === "placemark") {
      placemarks.push(parsePlacemark(child, registries));
    } else if (tag === "groundoverlay" || tag === "screenoverlay" || tag === "photooverlay") {
      const overlay = parseOverlayElement(child);
      if (overlay) overlays.push(overlay);
    }
  }

  return {
    id: el.getAttribute("id") || null,
    type: "Document",
    name: getText(el, "name"),
    description: getText(el, "description"),
    visibility: parseBoolean(getText(el, "visibility") || null, true),
    open: parseBoolean(getText(el, "open") || null, false),
    folders,
    placemarks,
    overlays,
    styles: registries.styles,
    styleMaps: registries.styleMaps,
    lookAt: parseLookAt(getChild(el, "LookAt")),
    camera: parseCamera(getChild(el, "Camera")),
  };
}

/* =============================================================================
 * SECTION 12: ROOT PARSER
 * ========================================================================== */

/**
 * Parses a full KML XML string (as produced by Google Earth Pro or any KML
 * 2.2-compliant tool) into a structured, render-ready result.
 *
 * Handles all three legal root shapes:
 *   <kml><Document>...</Document></kml>
 *   <kml><Folder>...</Folder></kml>
 *   <kml><Placemark>.../Placemark></kml>
 */
export function parseKmlDocument(xml: string): KmlParseResult {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, "application/xml");

  // Surface XML parsing errors explicitly rather than silently returning
  // an empty result, which would otherwise be indistinguishable from a
  // genuinely empty (but well-formed) KML document.
  const parserError = xmlDoc.getElementsByTagName("parsererror");
  if (parserError && parserError.length > 0) {
    throw new Error(`Invalid KML: ${parserError[0].textContent || "unknown XML parse error"}`);
  }

  const kmlRoot = xmlDoc.documentElement;

  const registries: StyleRegistries = { styles: {}, styleMaps: {} };

  let documentResult: KmlDocument | null = null;
  let folders: KmlFolder[] = [];
  let placemarks: KmlPlacemark[] = [];
  let overlays: KmlOverlay[] = [];
  let lookAt: KmlLookAt | null = null;
  let camera: KmlCamera | null = null;

  const documentEl = getChild(kmlRoot, "Document");
  const folderEl = getChild(kmlRoot, "Folder");
  const placemarkEl = getChild(kmlRoot, "Placemark");

  if (documentEl) {
    documentResult = parseDocument(documentEl, registries);
    folders = documentResult.folders;
    placemarks = documentResult.placemarks;
    overlays = documentResult.overlays;
    lookAt = documentResult.lookAt;
    camera = documentResult.camera;
  } else if (folderEl) {
    // A bare Folder at the root may still be preceded by sibling Styles.
    registerLocalStyles(kmlRoot, registries);
    const folder = parseFolder(folderEl, registries);
    folders = [folder];
    placemarks = folder.placemarks;
    overlays = folder.overlays;
  } else if (placemarkEl) {
    registerLocalStyles(kmlRoot, registries);
    const placemark = parsePlacemark(placemarkEl, registries);
    placemarks = [placemark];
  } else {
    // No recognized root feature; still honor any root-level Styles and
    // GroundOverlays that may exist outside a Document/Folder wrapper.
    registerLocalStyles(kmlRoot, registries);
    const overlayTags = ["GroundOverlay", "ScreenOverlay", "PhotoOverlay"];
    for (let i = 0; i < overlayTags.length; i++) {
      const overlayEls = getChildren(kmlRoot, overlayTags[i]);
      for (let j = 0; j < overlayEls.length; j++) {
        const overlay = parseOverlayElement(overlayEls[j]);
        if (overlay) overlays.push(overlay);
      }
    }
  }

  return {
    document: documentResult,
    folders,
    placemarks,
    styles: registries.styles,
    styleMaps: registries.styleMaps,
    overlays,
    lookAt,
    camera,
  };
}
